import Vue from 'vue';

import api from '@/api/learners';
import rootApi from '@/api';
import { GROUP_PERMISSIONS, ORGANIZATION_PERMISSIONS } from '@/constants';
import { getEmptyGroup, getEmptyOrganization } from '@/utils/permissions';
import vpaHistory from './vpa-history';

const namespaced = true;

export const getters = {
  groups: state => organizationId => state.groups[organizationId] || [],
  permissions: state => {
    const { organizations } = state.permissions;

    if (!organizations || Object.keys(organizations).length === 0) {
      return [getEmptyOrganization()];
    }

    return Object.keys(organizations).map(orgId => {
      const organization = organizations[orgId];

      const orgPermissions = Object.keys(organization.permissions)
        .sort((a, b) => ORGANIZATION_PERMISSIONS[b] - ORGANIZATION_PERMISSIONS[a])
        .map(key => ({ key, value: organization.permissions[key] }));

      const groups = Object.keys(organization.groups).map(groupId => {
        const group = organization.groups[groupId];
        const groupPermissions = Object.keys(group.permissions)
          .sort((a, b) => GROUP_PERMISSIONS[b] - GROUP_PERMISSIONS[a])
          .map(key => ({ key, value: group.permissions[key] }));

        return {
          ...group,
          id: Number.parseInt(groupId, 10),
          permissions: groupPermissions,
        };
      });

      return {
        ...organization,
        permissions: orgPermissions,
        id: Number.parseInt(orgId, 10),
        groups: groups.length > 0 ? groups : [getEmptyGroup()],
      };
    });
  },
};

export const actions = {
  FETCH_PROFILE: async ({ commit, dispatch }, { learnerId }) => {
    dispatch('wait/start', 'profile.fetching', { root: true });
    try {
      const profile = await api.fetchLearnerProfile({ learnerId });

      if (profile.organization && profile.organization.id) {
        const groups = await rootApi.fetchFeatureGroups(profile.organization.id);
        commit('SET_GROUPS', { organizationId: profile.organization.id, groups });
      }

      commit('SET_PROFILE', profile);

      return profile;
    } finally {
      dispatch('wait/end', 'profile.fetching', { root: true });
    }
  },

  FETCH_GROUPS: async ({ commit }, organizationId) => {
    if (!organizationId) {
      return;
    }

    const groups = await rootApi.fetchFeatureGroups(organizationId);

    commit('SET_GROUPS', { organizationId, groups });
  },

  FETCH_REPORT: async ({ commit }, { learnerId }) => {
    const report = await api.fetchLearnerReport({ learnerId });

    commit('SET_REPORT', { report });

    return report;
  },

  FETCH_ENGAGEMENT_REPORT: async ({ commit }, { learnerId }) => {
    const [report] = await api.fetchEngagementReport({ learnerId });

    commit('SET_ENGAGEMENT', { report });

    return report;
  },

  FETCH_PERMISSIONS: async ({ commit, dispatch }, userId) => {
    dispatch('wait/start', 'profile.fetchingPermisisons', { root: true });

    try {
      const permissions = await api.fetchUserPermissions(userId);

      commit('SET_PERMISSIONS', permissions);
    } finally {
      dispatch('wait/end', 'profile.fetchingPermisisons', { root: true });
    }
  },

  UPDATE_PERMISSIONS: async ({ commit, dispatch }, { userId, permissions }) => {
    dispatch('wait/start', 'profile.savingPermisisons', { root: true });

    try {
      await api.updateUserPermissions(userId, permissions);

      commit('SET_PERMISSIONS', permissions);
    } finally {
      dispatch('wait/end', 'profile.savingPermisisons', { root: true });
    }
  },

  RESEND_ACTIVATION_EMAIL: (context, { email }) => {
    return api.resendActivationEmail(email);
  },

  RESET_PASSWORD: (context, { learnerEmail }) => {
    return api.resetPassword(learnerEmail);
  },

  MAKE_VPA_AVAILABLE_NOW: (context, { email }) => {
    return api.makeVpaAvailableNow(email);
  },

  UPDATE_FEATURE_GROUP: async ({ commit, state }, { learnerId, featureGroup }) => {
    await api.setFeatureGroup({ learnerId, featureGroup });

    commit('SET_LEARNER_FEATURE_GROUP', { learnerId, featureGroup });

    return state.profile;
  },
};

export const mutations = {
  SET_PROFILE(state, profile) {
    state.profile = profile;
  },

  SET_PERMISSIONS(state, permissions) {
    state.permissions = permissions;
  },

  SET_GROUPS(state, { organizationId, groups = [] }) {
    if (!organizationId) {
      return;
    }

    Vue.set(state.groups, organizationId, groups);
  },

  SET_REPORT(state, { report }) {
    state.report = report;
  },

  SET_ENGAGEMENT(state, { report }) {
    state.engagement = report;
  },

  CLEAR_PROFILE(state) {
    state.profile = null;
    state.groups = [];
    state.engagement = null;
    state.report = null;
  },

  CLEAR_REPORT(state) {
    state.report = null;
  },

  SET_LEARNER_FEATURE_GROUP(state, { learnerId, featureGroup }) {
    if (state.profile.id === learnerId) {
      state.profile = { ...state.profile, group: featureGroup };
    }
  },
};

const state = {
  groups: {},
  profile: null,
  report: null,
  engagement: null,
  permissions: {},
};

export default {
  namespaced,
  actions,
  getters,
  mutations,
  state,
  modules: {
    vpaHistory,
  },
};
